/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import { resolverAtribucionFactura } from "../../utils/atribucionFactura";
import { vincularFacturaEmitida, desvincularVinculacion } from "../../utils/conciliacionSII";
import { esNotaCredito, tipoDocumentoCorto } from "../../utils/dte";
import { getLineaIdPorClave, tipoLineaDeConcepto } from "../../utils/lineasFacturacion/getLineaId";
import {
  calcularRepartoInicial,
  validarReparto,
  aplicarResto,
  claveLinea,
} from "../../utils/lineasFacturacion/repartoLineasFactura";
import { resolverNombreRutNegocio } from "../../utils/lineasFacturacion/resolverNombreNegocio";
import { construirExclusionNcAnulaError } from "../../utils/ncAnulaError";
import { formatRut } from "../../utils/rut";
import { client } from "../../utils/supabase";
import { tieneEdificiosAsignados } from "../../utils/tieneEdificiosAsignados";

const MESES_LABELS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

// Orden del panel: propias primero por tipo (gestión → opex → hardware), luego las torres hermanas.
const TIPO_ORDER = { Gestión: 0, "HW-OPEX": 1, "HW-CAPEX": 2 };
const TIPO_LINEA_A_TYPE = { gestion: "Gestión", opex: "HW-OPEX", hardware: "HW-CAPEX", contenidos: "Gestión" };

const etiquetaTipoConcepto = (type) =>
  type === "Gestión" ? "Gestión" : type === "HW-OPEX" ? "OPEX" : type === "HW-CAPEX" ? "Hardware" : type || "—";

const normRut = (r) =>
  String(r || "")
    .replace(/[\s.-]/g, "")
    .toLowerCase();

const formatMonto = (monto, prefijo = "$") => {
  if (monto == null) return "-";
  return `${prefijo} ${Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [, month, day] = fechaStr.split("-");
  return `${day}/${month}`;
};

// periodo_tributario del DTE (YYYYMM) → "MMM-YY", para distinguir dos facturas del mismo cliente/monto
// por período (caso 554/570). Fallback: mes/año de la fecha de emisión.
const formatPeriodo = (dte) => {
  const p = dte?.periodo_tributario != null ? String(dte.periodo_tributario) : null;
  if (p && p.length === 6) {
    const yy = p.slice(2, 4);
    const mm = parseInt(p.slice(4, 6), 10);
    if (mm >= 1 && mm <= 12) return `${MESES_LABELS[mm - 1]}-${yy}`;
  }
  if (dte?.fecha_emision) {
    const [y, m] = dte.fecha_emision.split("-");
    const mm = parseInt(m, 10);
    if (mm >= 1 && mm <= 12) return `${MESES_LABELS[mm - 1]}-${String(y).slice(-2)}`;
  }
  return "—";
};

const thStyle = {
  padding: "8px",
  textAlign: "center",
  color: "#fff",
  fontSize: "0.65rem",
  textTransform: "uppercase",
  fontWeight: 600,
};

const SeleccionarDTEModal = ({
  isOpen,
  toggle,
  oportunidad,
  onVinculada,
  conceptosHermanos = [],
  lineasDelNegocio = [],
}) => {
  const { pais, id: userId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const [dtes, setDtes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(null);
  const [filtroMes, setFiltroMes] = useState(null); // null = todo el año, 1-12 = mes específico
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  // ── L6/D4: panel de atribución por líneas (solo cartelería) ──
  const esCarteleriaOp = !!oportunidad?.negocio_id;
  const [facturaSelId, setFacturaSelId] = useState(null); // DTE elegido para atribuir en el panel
  const [montos, setMontos] = useState({}); // clave de línea → monto a aplicar
  const [lineasExtra, setLineasExtra] = useState([]); // líneas ya vinculadas a la factura que no vienen por props
  const [rutPorNegocio, setRutPorNegocio] = useState(new Map());
  const [nombrePorNegocio, setNombrePorNegocio] = useState(new Map());
  const [guardando, setGuardando] = useState(false);
  const autoSelHechoRef = useRef(false);

  const anoNum = oportunidad?.facturafecha
    ? oportunidad.facturafecha.split("-")[0]
    : oportunidad?.ano || new Date().getFullYear().toString();

  // Mes de la venta (para destacar el pill correspondiente)
  const mesOportunidad = oportunidad?.mes ? parseInt(oportunidad.mes, 10) : null;

  const cargarDTEs = useCallback(async () => {
    if (!oportunidad || !pais) return;
    setCargando(true);
    try {
      const { data } = await client
        .from("facturas_sii_emitidas")
        .select(
          "*, facturas_sii_vinculaciones(id, venta_tipo, venta_ref_id, venta_ref_index, linea_id, monto_aplicado)"
        )
        .eq("pais", pais)
        .gte("fecha_emision", `${anoNum}-01-01`)
        .lte("fecha_emision", `${anoNum}-12-31`)
        .order("fecha_emision", { ascending: true })
        .order("folio", { ascending: true });

      const rows = data || [];
      setDtes(rows);

      // Filtros por defecto invertidos: abrir en el mes de la venta + estado Pendiente (antes:
      // Todo el año + Todas). Se calcula acá, con la data recién cargada, para el guard 10.177:
      // si el mes de la venta NO tiene documentos, caé a "Todo el año" en vez de una tabla vacía.
      const mesTieneDocs =
        mesOportunidad != null &&
        rows.some((d) => d.fecha_emision && parseInt(d.fecha_emision.split("-")[1], 10) === mesOportunidad);
      setFiltroMes(mesTieneDocs ? mesOportunidad : null);
      setFiltroEstado("pendiente");
    } catch (err) {
      console.error("Error cargando DTEs:", err);
      toast.error(`Error cargando documentos del ${enteFiscal}`);
    } finally {
      setCargando(false);
    }
  }, [oportunidad, pais, anoNum, enteFiscal, mesOportunidad]);

  useEffect(() => {
    if (isOpen && oportunidad) {
      cargarDTEs();
    }
  }, [isOpen, oportunidad, cargarDTEs]);

  useEffect(() => {
    if (isOpen) {
      // filtroMes / filtroEstado los fija cargarDTEs con la data cargada (defaults invertidos +
      // guard 10.177). Acá se limpian búsqueda y el estado del panel.
      setFiltroBusqueda("");
      setFacturaSelId(null);
      setMontos({});
      setLineasExtra([]);
      autoSelHechoRef.current = false;
    }
  }, [isOpen]);

  // Conteo de DTEs por mes (índice 0 = enero)
  const countsByMonth = MESES_LABELS.map((_, idx) => {
    const mes = idx + 1;
    return dtes.filter((d) => {
      if (!d.fecha_emision) return false;
      return parseInt(d.fecha_emision.split("-")[1], 10) === mes;
    }).length;
  });

  // Pares NC↔factura `anula_error` (fuente ÚNICA, regla 10.223 — prohibido reimplementar la
  // detección `tipo_dte=61 + tipo_relacion_nc + documento_referenciado_id` inline). Los tres campos
  // existen igual en emitidas. Solo consumimos los dos Sets CON-ref; `notasSinRef` NO se usa.
  const { facturasAnuladas, notasCredito } = construirExclusionNcAnulaError(dtes);
  const ncFolioPorFactura = new Map();
  for (const d of dtes) {
    if (!notasCredito.has(Number(d.id))) continue;
    if (d.documento_referenciado_id != null) ncFolioPorFactura.set(Number(d.documento_referenciado_id), d.folio);
  }

  // Verificar si un DTE está vinculado a ESTA oportunidad
  const estaVinculadaAEsta = useCallback(
    (dte) => {
      const vincs = dte.facturas_sii_vinculaciones || [];
      if (!oportunidad) return false;

      if (oportunidad.negocio_id) {
        const tipoCart = oportunidad.type === "HW-CAPEX" ? "negocios_carteleria_hw" : "negocios_carteleria";
        return vincs.some(
          (v) =>
            v.venta_tipo === tipoCart &&
            String(v.venta_ref_id) === String(oportunidad.negocio_id) &&
            String(v.venta_ref_index ?? "") === String(oportunidad.sii_ref_index ?? "")
        );
      }
      if (oportunidad.type === "pauta") {
        return vincs.some((v) => v.venta_tipo === "sales" && String(v.venta_ref_id) === String(oportunidad.id));
      }
      if (oportunidad.type === "BTL") {
        return vincs.some((v) => v.venta_tipo === "sales_btl" && String(v.venta_ref_id) === String(oportunidad.id));
      }
      if (oportunidad.type === "contrato") {
        return vincs.some(
          (v) =>
            v.venta_tipo === "sales_contratos" &&
            String(v.venta_ref_id) === String(oportunidad.contrato_id) &&
            String(v.venta_ref_index ?? "") === String(oportunidad.mes_index ?? "")
        );
      }
      return false;
    },
    [oportunidad]
  );

  // Derivar venta_tipo y ref_id desde un concepto (por defecto el ancla `oportunidad`).
  const getVinculacionData = (concepto = oportunidad) => {
    if (concepto.negocio_id) {
      return {
        venta_tipo: concepto.type === "HW-CAPEX" ? "negocios_carteleria_hw" : "negocios_carteleria",
        venta_ref_id: Number(concepto.negocio_id),
        venta_ref_index: concepto.sii_ref_index ?? concepto.entry_index ?? null,
      };
    }
    if (concepto.type === "pauta") {
      return { venta_tipo: "sales", venta_ref_id: Number(concepto.id), venta_ref_index: null };
    }
    if (concepto.type === "BTL") {
      return { venta_tipo: "sales_btl", venta_ref_id: Number(concepto.id), venta_ref_index: null };
    }
    if (concepto.type === "contrato") {
      return {
        venta_tipo: "sales_contratos",
        venta_ref_id: Number(concepto.contrato_id),
        venta_ref_index: concepto.mes_index ?? null,
      };
    }
    return null;
  };

  // Verificar si este concepto ya está vinculado a ALGÚN DTE (en los datos cargados)
  const conceptoYaVinculado = () => dtes.some((dte) => estaVinculadaAEsta(dte));

  // L6 tramo 2: resuelve la LÍNEA del concepto (sin tocar el array por posición) y llama a
  // vincularFacturaEmitida (→ vincular_sii_linea, que deriva el venta_ref_index espejo de linea_id vía
  // nc_posicion_de_linea). Gestión/opex de un mes nuevo se crean por `upsert_linea_facturacion` (única
  // forma de crear una línea, 10.1). HW-CAPEX debe tener su tramo ya datado (su alta la hace
  // FacturacionHardwareModal); si no, error visible (10.177), nunca dateo por índice.
  const ejecutarVinculacion = async (dte, concepto, vincData) => {
    if (concepto.negocio_id) {
      const tipoLinea = concepto.type === "HW-CAPEX" ? "hardware" : concepto.type === "HW-OPEX" ? "opex" : "gestion";
      let lineaId = concepto.linea_id ?? null;

      if (lineaId == null) {
        if (tipoLinea === "hardware") {
          throw new Error(
            `El tramo de hardware ${concepto.mes}-${concepto.ano} no tiene línea de facturación. Datalo en Facturación de Hardware primero.`
          );
        }
        const { data: nuevoLineaId, error: upErr } = await client.rpc("upsert_linea_facturacion", {
          p_negocio_id: String(concepto.negocio_id),
          p_tipo: tipoLinea,
          p_ano: String(concepto.ano),
          p_mes: String(concepto.mes),
          p_tramo: 1,
          p_campos: { facturanumero: String(dte.folio), facturafecha: dte.fecha_emision },
          p_usuario_id: parseInt(userId, 10) || null,
        });
        if (upErr) throw upErr;
        lineaId = nuevoLineaId;
      }

      if (lineaId == null) {
        throw new Error(
          `No se encontró la línea de facturación para ${concepto.type} ${concepto.mes}-${concepto.ano}. Reintentá; si persiste, avisá.`
        );
      }
      vincData.linea_id = lineaId;
    }

    await vincularFacturaEmitida(dte.id, {
      ...vincData,
      cliente_id: concepto.client_id || null,
      usuario_id: userId,
    });

    // Espejo del venta_ref_index para el patch de la grilla (la RPC lo derivó de linea_id). Lectura del
    // puente autoritativo — no se calcula posición en el cliente.
    if (concepto.negocio_id && vincData.linea_id != null) {
      const { data: puente } = await client
        .from("facturas_sii_vinculaciones")
        .select("venta_ref_index")
        .eq("factura_sii_emitida_id", dte.id)
        .eq("linea_id", vincData.linea_id)
        .maybeSingle();
      if (puente?.venta_ref_index != null) vincData.venta_ref_index = puente.venta_ref_index;
    }
  };

  // Payload para que el consumidor (VentasFacturas) parchee la fila por ID (10.7/10.234) tras vincular.
  // lineaId (L6 tramo 4): la refKey de lectura ahora es `linea:<linea_id>` para cartelería/hardware —
  // sin este patch, un mes/tramo recién creado (linea_id resuelto por ejecutarVinculacion) no matchea
  // `_byRef` hasta refrescar la página. siiRefIndex se sigue propagando para el espejo legacy.
  const construirPatchVinculacion = (concepto, vincData) => ({
    opId: concepto.id,
    opType: concepto.type,
    esCarteleria: !!concepto.negocio_id,
    siiRefIndex: vincData.venta_ref_index ?? null,
    lineaId: vincData.linea_id ?? null,
  });

  // linea_id de un concepto de cartelería para desvincular por línea. Fuente: linea_id (loader
  // L2, canónico desde L6 tramo 4); fallback DB por clave natural (gestión/opex). Devuelve null para no-cartelería.
  const resolverLineaIdConcepto = async (concepto) => {
    if (!concepto?.negocio_id) return null;
    if (concepto.linea_id != null) return concepto.linea_id;
    if (concepto.type === "HW-CAPEX") return null;
    const tipoLinea = concepto.type === "HW-OPEX" ? "opex" : "gestion";
    return getLineaIdPorClave({
      negocio_id: concepto.negocio_id,
      tipo: tipoLinea,
      ano: concepto.ano,
      mes: concepto.mes,
      tramo: 1,
    });
  };

  const handleDesvincular = async (dte) => {
    const vincData = getVinculacionData();
    if (!vincData) return;
    if (oportunidad.negocio_id) vincData.linea_id = await resolverLineaIdConcepto(oportunidad);
    setProcesando(dte.id);
    try {
      await desvincularVinculacion(dte.id, vincData);
      setDtes((prev) =>
        prev.map((d) => {
          if (d.id !== dte.id) return d;
          const nuevasVincs = (d.facturas_sii_vinculaciones || []).filter(
            (v) =>
              !(
                v.venta_tipo === vincData.venta_tipo &&
                String(v.venta_ref_id) === String(vincData.venta_ref_id) &&
                String(v.venta_ref_index ?? "") === String(vincData.venta_ref_index ?? "")
              )
          );
          return {
            ...d,
            facturas_sii_vinculaciones: nuevasVincs,
            estado_vinculacion: nuevasVincs.length === 0 ? "pendiente" : d.estado_vinculacion,
          };
        })
      );
      toast.success(`Desvinculada de folio ${dte.folio}`);
      onVinculada?.();
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular");
    } finally {
      setProcesando(null);
    }
  };

  // Reasigna un DTE ya vinculado (a otra oportunidad o vía conciliación bancaria) hacia la
  // oportunidad actual. Camino NO-cartelería (pauta/contrato): la cartelería se maneja por el panel.
  const handleEditar = async (dte) => {
    const vincData = getVinculacionData();
    if (!vincData) return;

    if (conceptoYaVinculado()) {
      toast.error("Este concepto ya está vinculado a otra factura. Desvinculá primero.");
      return;
    }

    if (oportunidad.type === "contrato" && !tieneEdificiosAsignados(oportunidad.edificios_ids)) {
      toast.error(
        `No se puede vincular: la cuota de ${oportunidad.mes}-${oportunidad.ano} no tiene edificios asignados. Asigná los edificios antes de vincular la factura.`
      );
      return;
    }
    if (oportunidad.type === "pauta" && !tieneEdificiosAsignados(oportunidad.edificiosparticipantes)) {
      toast.error(
        "No se puede vincular: la venta no tiene edificios asignados. Asigná los edificios antes de vincular la factura."
      );
      return;
    }

    setProcesando(dte.id);
    try {
      for (const v of dte.facturas_sii_vinculaciones || []) {
        await desvincularVinculacion(dte.id, {
          venta_tipo: v.venta_tipo,
          venta_ref_id: v.venta_ref_id,
          venta_ref_index: v.venta_ref_index,
          linea_id: v.linea_id ?? null,
        });
      }
      await client
        .from("facturas_sii_emitidas")
        .update({
          estado_vinculacion: "pendiente",
          venta_tipo: null,
          venta_ref_id: null,
          venta_ref_index: null,
          cliente_id: null,
          vinculado_por: null,
          vinculado_at: null,
        })
        .eq("id", dte.id);

      await ejecutarVinculacion(dte, oportunidad, vincData);
      const nuevaVinc = {
        id: Date.now(),
        venta_tipo: vincData.venta_tipo,
        venta_ref_id: vincData.venta_ref_id,
        venta_ref_index: vincData.venta_ref_index,
      };
      setDtes((prev) =>
        prev.map((d) =>
          d.id === dte.id ? { ...d, estado_vinculacion: "vinculada", facturas_sii_vinculaciones: [nuevaVinc] } : d
        )
      );
      toast.success(`Reasignada a folio ${dte.folio}`);
      // Se espera el refresh del consumidor (recargarSiiMap) antes de cerrar: la grilla ya reubica la
      // fila cuando el modal desaparece. Solo en éxito — con error el modal queda abierto.
      await onVinculada?.(construirPatchVinculacion(oportunidad, vincData));
      toggle();
    } catch (err) {
      console.error("Error reasignando:", err);
      toast.error(err?.code === "EDIFICIOS_FALTANTES" ? err.message : "Error al reasignar");
    } finally {
      setProcesando(null);
    }
  };

  const handleVincularVenta = async (dte) => {
    // Camino NO-cartelería (pauta/BTL/contrato): una fila del puente, por venta.
    const vincData = getVinculacionData();
    if (!vincData) return;
    if (conceptoYaVinculado()) {
      toast.error("Este concepto ya está vinculado a otra factura. Desvinculá primero.");
      return;
    }
    setProcesando(dte.id);
    try {
      await ejecutarVinculacion(dte, oportunidad, vincData);
      const nuevaVinc = {
        id: Date.now(),
        venta_tipo: vincData.venta_tipo,
        venta_ref_id: vincData.venta_ref_id,
        venta_ref_index: vincData.venta_ref_index,
      };
      setDtes((prev) =>
        prev.map((d) =>
          d.id === dte.id
            ? {
                ...d,
                estado_vinculacion: "vinculada",
                facturas_sii_vinculaciones: [...(d.facturas_sii_vinculaciones || []), nuevaVinc],
              }
            : d
        )
      );
      toast.success(`Vinculada a folio ${dte.folio}`);
      // Ídem handleEditar: cerrar recién cuando terminó el refresh, y solo en éxito.
      await onVinculada?.(construirPatchVinculacion(oportunidad, vincData));
      toggle();
    } catch (err) {
      console.error("Error vinculando:", err);
      toast.error(err?.code === "EDIFICIOS_FALTANTES" ? err.message : err?.message || "Error al vincular");
    } finally {
      setProcesando(null);
    }
  };

  // ─────────────────────────── PANEL DE ATRIBUCIÓN (cartelería, D4) ───────────────────────────

  const facturaSel = useMemo(() => dtes.find((d) => d.id === facturaSelId) || null, [dtes, facturaSelId]);

  // Al abrir sobre una cartelería YA vinculada, autoseleccionar su factura (una sola vez por apertura).
  useEffect(() => {
    if (!isOpen || !esCarteleriaOp || autoSelHechoRef.current) return;
    if (dtes.length === 0) return;
    const linked = dtes.find((d) => estaVinculadaAEsta(d));
    if (linked) setFacturaSelId(linked.id);
    autoSelHechoRef.current = true;
  }, [isOpen, esCarteleriaOp, dtes, estaVinculadaAEsta]);

  // Candidatos base = líneas del propio negocio del mes (todos los tipos) + torres hermanas. Dedupe por
  // clave de línea. Cada uno conserva su concepto (op) para reusar ejecutarVinculacion (10.1).
  const candidatosBase = useMemo(() => {
    if (!esCarteleriaOp) return [];
    const map = new Map();
    const push = (c, esPropio) => {
      const key = claveLinea({
        linea_id: c.linea_id ?? null,
        negocio_id: c.negocio_id,
        tipo: tipoLineaDeConcepto(c),
        ano: c.ano,
        mes: String(c.mes).padStart(2, "0"),
        tramo: 1,
      });
      if (!map.has(key)) map.set(key, { concepto: c, clave: key, linea_id: c.linea_id ?? null, esPropio });
    };
    for (const c of lineasDelNegocio) push(c, true);
    for (const c of conceptosHermanos) push(c, String(c.negocio_id) === String(oportunidad?.negocio_id));
    return [...map.values()];
  }, [esCarteleriaOp, lineasDelNegocio, conceptosHermanos, oportunidad]);

  // Orden del panel: propias primero (por tipo), luego hermanas con el RUT de la factura primero,
  // después el resto alfabético. Puro (no muta la entrada).
  const ordenarLineas = useCallback(
    (lista, rutMap, rutFactura) => {
      const nombre = (c) => c.concepto.cliente || nombrePorNegocio.get(String(c.concepto.negocio_id)) || "";
      return [...lista].sort((a, b) => {
        if (a.esPropio !== b.esPropio) return a.esPropio ? -1 : 1;
        if (a.esPropio) return (TIPO_ORDER[a.concepto.type] ?? 9) - (TIPO_ORDER[b.concepto.type] ?? 9);
        const am = rutFactura && rutMap.get(String(a.concepto.negocio_id)) === rutFactura ? 0 : 1;
        const bm = rutFactura && rutMap.get(String(b.concepto.negocio_id)) === rutFactura ? 0 : 1;
        if (am !== bm) return am - bm;
        return nombre(a).localeCompare(nombre(b));
      });
    },
    [nombrePorNegocio]
  );

  // Al elegir/cambiar la factura: (1) traer las líneas ya vinculadas a ella que no vengan por props
  // (torres ya repartidas), (2) resolver RUT/nombre del cliente de cada negocio candidato (orden), y
  // (3) sembrar los montos por defecto (aplicado si ya está atribuida; neto entero en la 1ª si no).
  useEffect(() => {
    if (!esCarteleriaOp || !facturaSel) {
      setMontos({});
      setLineasExtra([]);
      return;
    }
    let cancel = false;
    (async () => {
      const puente = facturaSel.facturas_sii_vinculaciones || [];
      const baseClaves = new Set(candidatosBase.map((c) => c.clave));

      // (1) extras: líneas de cartelería ligadas a esta factura fuera de props (torres ya repartidas)
      const extrasRef = puente
        .filter(
          (v) =>
            v.linea_id != null &&
            (v.venta_tipo === "negocios_carteleria" || v.venta_tipo === "negocios_carteleria_hw") &&
            !baseClaves.has(`L:${v.linea_id}`)
        )
        .map((v) => ({ linea_id: v.linea_id, venta_ref_index: v.venta_ref_index }));

      let extras = [];
      if (extrasRef.length > 0) {
        const { data: lrows } = await client
          .from("lineas_facturacion")
          .select("id, negocio_id, tipo, ano, mes, tramo, cuota_unitaria, moneda, cantidad")
          .in(
            "id",
            extrasRef.map((x) => x.linea_id)
          );
        extras = (lrows || []).map((l) => {
          const refIdx = extrasRef.find((x) => String(x.linea_id) === String(l.id))?.venta_ref_index ?? null;
          const type = TIPO_LINEA_A_TYPE[l.tipo] || "Gestión";
          return {
            clave: `L:${l.id}`,
            linea_id: l.id,
            esPropio: String(l.negocio_id) === String(oportunidad?.negocio_id),
            concepto: {
              id: `linea-${l.id}`,
              negocio_id: l.negocio_id,
              type,
              ano: l.ano,
              mes: l.mes,
              sii_ref_index: refIdx,
              entry_index: refIdx,
              linea_id: l.id,
              // sin `cliente` (no viene de una fila de la grilla): el nombre se resuelve por
              // nombrePorNegocio (tipo_vertical-aware) en lineasPanel.
              cliente: null,
              client_id: null,
              importe: (Number(l.cuota_unitaria) || 0) * (Number(l.cantidad) || 1),
              moneda_dolar: l.moneda === "USD",
            },
          };
        });
      }
      if (cancel) return;
      setLineasExtra(extras);

      // (2) RUT + nombre por negocio, SEGÚN EL TIPO del negocio (no `referencia_id` a ciegas — 10.7).
      // `referencia_id` apunta a `clientes` (venta directa, TD) o a `edificios` (EO/OT edificio); los ids
      // colisionan entre tablas (caso negocio 11 EO ref 156: edificio "De La Industria" vs cliente
      // "SANITAS"). Se resuelve igual que el loader de la grilla (useVFDataLoading): TD → clientes;
      // resto → edificios primero, cliente como fallback. El RUT solo existe cuando la referencia es un
      // cliente (edificios no tiene RUT) → EO no ordena por RUT (sin error). El nombre es solo FALLBACK
      // para las líneas "extra"; las de la grilla usan `concepto.cliente` (ver lineasPanel).
      const negocioIds = [...new Set([...candidatosBase, ...extras].map((c) => String(c.concepto.negocio_id)))];
      const rutMap = new Map();
      const nombreMap = new Map();
      if (negocioIds.length > 0) {
        const { data: negs } = await client
          .from("negocios_carteleria")
          .select("id, tipo_vertical, referencia_id, nombre_proyecto")
          .in("id", negocioIds);
        const refIds = [...new Set((negs || []).map((n) => n.referencia_id).filter((x) => x != null))];
        let clientesById = new Map();
        let edificiosById = new Map();
        if (refIds.length > 0) {
          const [{ data: cls }, { data: edif }] = await Promise.all([
            client.from("clientes").select("id, rut, nombre").in("id", refIds),
            client.from("edificios").select("id, edificio").in("id", refIds),
          ]);
          clientesById = new Map((cls || []).map((c) => [String(c.id), c]));
          edificiosById = new Map((edif || []).map((e) => [String(e.id), e]));
        }
        for (const n of negs || []) {
          const { nombre, rut } = resolverNombreRutNegocio(n, { clientesById, edificiosById });
          if (nombre) nombreMap.set(String(n.id), nombre);
          if (rut) rutMap.set(String(n.id), normRut(rut));
        }
      }
      if (cancel) return;
      setRutPorNegocio(rutMap);
      setNombrePorNegocio(nombreMap);

      // (3) montos por defecto, sobre la lista ORDENADA (la 1ª línea propia toma el neto entero)
      const neto = resolverAtribucionFactura(facturaSel, puente).neto;
      const aplicadoPorClave = {};
      for (const v of puente)
        if (v.linea_id != null) aplicadoPorClave[`L:${v.linea_id}`] = Number(v.monto_aplicado) || 0;
      const ordenadas = ordenarLineas([...candidatosBase, ...extras], rutMap, normRut(facturaSel.rut_receptor));
      if (cancel) return;
      setMontos(calcularRepartoInicial({ lineas: ordenadas, aplicadoPorClave, neto }));
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaSel, esCarteleriaOp, candidatosBase, oportunidad]);

  // Líneas del panel (ordenadas + anotadas con lo aplicado en la factura seleccionada).
  const lineasPanel = useMemo(() => {
    if (!esCarteleriaOp || !facturaSel) return [];
    const puente = facturaSel.facturas_sii_vinculaciones || [];
    const aplicadoMap = {};
    for (const v of puente) if (v.linea_id != null) aplicadoMap[`L:${v.linea_id}`] = Number(v.monto_aplicado) || 0;
    const ordenadas = ordenarLineas(
      [...candidatosBase, ...lineasExtra],
      rutPorNegocio,
      normRut(facturaSel.rut_receptor)
    );
    return ordenadas.map((c) => ({
      ...c,
      aplicado: aplicadoMap[c.clave] || 0,
      // Nombre = el de la GRILLA (`concepto.cliente`, ya resuelto por el loader tipo_vertical-aware);
      // nombrePorNegocio es solo fallback para las líneas "extra" (no vienen de una fila de la grilla).
      label: `${c.concepto.cliente || nombrePorNegocio.get(String(c.concepto.negocio_id)) || `Negocio ${c.concepto.negocio_id}`} · ${etiquetaTipoConcepto(c.concepto.type)}`,
      cuotaRef: c.concepto.importe != null ? Number(c.concepto.importe) : null,
      esUSD: !!c.concepto.moneda_dolar,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esCarteleriaOp, facturaSel, candidatosBase, lineasExtra, rutPorNegocio, nombrePorNegocio]);

  const atribucion = useMemo(
    () => (facturaSel ? resolverAtribucionFactura(facturaSel, facturaSel.facturas_sii_vinculaciones || []) : null),
    [facturaSel]
  );
  const netoSel = atribucion?.neto || 0;
  const validacion = useMemo(
    () => validarReparto({ lineas: lineasPanel, montos, neto: netoSel }),
    [lineasPanel, montos, netoSel]
  );

  const refrescarFacturaSel = async (facturaId) => {
    const { data } = await client
      .from("facturas_sii_emitidas")
      .select("*, facturas_sii_vinculaciones(id, venta_tipo, venta_ref_id, venta_ref_index, linea_id, monto_aplicado)")
      .eq("id", facturaId)
      .single();
    if (data) setDtes((prev) => prev.map((d) => (d.id === data.id ? data : d)));
  };

  const handleAplicarResto = (claveDestino) => {
    setMontos((prev) => aplicarResto({ lineas: lineasPanel, montos: prev, neto: netoSel, claveDestino }));
  };

  const handleGuardarPanel = async () => {
    if (!facturaSel) return;
    if (!validacion.ok) {
      toast.error(validacion.errores[0]);
      return;
    }
    setGuardando(true);
    const patches = [];
    const fallidos = [];
    try {
      for (const cand of lineasPanel) {
        const target = Math.round(Number(montos[cand.clave]) || 0);
        const aplicadoActual = cand.aplicado || 0;
        const estaLinked = (facturaSel.facturas_sii_vinculaciones || []).some(
          (v) => v.linea_id != null && `L:${v.linea_id}` === cand.clave
        );
        try {
          if (target <= 0 && estaLinked) {
            const vd = getVinculacionData(cand.concepto);
            const linea_id = cand.linea_id ?? (await resolverLineaIdConcepto(cand.concepto));
            await desvincularVinculacion(facturaSel.id, { ...vd, linea_id });
          } else if (target > 0 && (!estaLinked || target !== aplicadoActual)) {
            const vd = getVinculacionData(cand.concepto);
            await ejecutarVinculacion(facturaSel, cand.concepto, { ...vd, monto_aplicado: target });
            patches.push(construirPatchVinculacion(cand.concepto, vd));
          }
        } catch (err) {
          console.error("Error atribuyendo línea", cand.clave, err);
          fallidos.push({ cand, msg: err?.code === "EDIFICIOS_FALTANTES" ? err.message : err?.message || "error" });
        }
      }

      await refrescarFacturaSel(facturaSel.id);
      if (patches.length > 0) await onVinculada?.(patches);

      if (fallidos.length === 0) {
        toast.success(`Atribución guardada (folio ${facturaSel.folio})`);
        // Cierra solo si se guardó al menos una vinculación y ninguna línea falló; con errores el
        // panel queda abierto para que el usuario vea qué línea no entró y reintente.
        if (patches.length > 0) toggle();
      } else {
        toast.error(`Guardado con ${fallidos.length} error(es): ${fallidos.map((f) => f.cand.label).join(", ")}`);
      }
    } finally {
      setGuardando(false);
    }
  };

  if (!oportunidad) return null;

  // Estilos compartidos para pills
  const pillBase = {
    padding: "3px 9px",
    fontSize: "0.68rem",
    fontWeight: 600,
    border: "1px solid #dee2e6",
    borderRadius: 4,
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  };

  const badgeEstado = (estado) => {
    const map = {
      total: { bg: "#e8f8f0", fg: "#1e8449", txt: "Total" },
      parcial: { bg: "#fef5e7", fg: "#b9770e", txt: "Parcial" },
      sin_vincular: { bg: "#f2f3f4", fg: "#7f8c8d", txt: "Sin atribuir" },
    };
    const s = map[estado] || map.sin_vincular;
    return (
      <span
        style={{
          background: s.bg,
          color: s.fg,
          padding: "2px 10px",
          borderRadius: 12,
          fontWeight: 700,
          fontSize: "0.72rem",
        }}
      >
        ● {s.txt}
      </span>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" style={{ maxWidth: 1300 }}>
      <ModalBody>
        {/* Datos de la venta */}
        <div
          style={{
            background: "#000",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.85rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            justifyContent: "center",
          }}
        >
          <span>
            <strong>Venta:</strong> {(oportunidad.cliente || "").toUpperCase()}
          </span>
          <span>
            <strong>Tipo:</strong> {oportunidad.type}
          </span>
          <span>
            <strong>Mes venta:</strong>{" "}
            {mesOportunidad ? `${MESES_LABELS[mesOportunidad - 1]}/${String(anoNum).slice(-2)}` : "-"}
          </span>
          <span>
            <strong>Importe:</strong> {formatMonto(oportunidad.importe, oportunidad.moneda_dolar ? "US$" : "$")}
          </span>
          <span>
            <strong>Factura:</strong> {oportunidad.facturanumero || "Sin facturar"}
          </span>
        </div>

        {/* PANEL DE ATRIBUCIÓN (D4) — solo cartelería. Al elegir una factura de la tabla, se reparte
            su neto entre las líneas del negocio del mes + las torres hermanas, con monto por línea. */}
        {esCarteleriaOp && !facturaSel && (
          <div
            style={{
              border: "1px dashed #cbd5e1",
              background: "#f8fafc",
              borderRadius: 6,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: "0.8rem",
              color: "#475569",
            }}
          >
            Elegí una factura de la tabla ("Seleccionar") para atribuir su neto a las líneas del negocio del mes.
          </div>
        )}

        {esCarteleriaOp && facturaSel && (
          <div
            style={{
              border: "1px solid #d6dbe0",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 16,
              background: "#fbfcfd",
            }}
          >
            {/* Encabezado de la factura seleccionada */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontWeight: 700 }}>
                Factura N.º {facturaSel.folio} · emisión {formatFecha(facturaSel.fecha_emision)} · período{" "}
                {formatPeriodo(facturaSel)}
              </span>
              <span style={{ marginLeft: "auto" }}>{badgeEstado(atribucion?.estado)}</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 28px", marginBottom: 10, fontSize: "0.85rem" }}>
              <span>
                <strong>Neto</strong> {formatMonto(atribucion?.neto)}
              </span>
              <span>
                <strong>Aplicado</strong> {formatMonto(atribucion?.aplicado)}
              </span>
              <span>
                <strong>Saldo</strong> {formatMonto(atribucion?.saldo)}
              </span>
            </div>

            {/* Líneas del negocio del mes (+ torres hermanas), con monto editable por línea */}
            <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#2c3e50" }}>
                  <th style={{ ...thStyle, textAlign: "left" }}>Concepto</th>
                  <th style={thStyle}>Cuota (ref.)</th>
                  <th style={thStyle}>Aplicado</th>
                  <th style={thStyle}>Monto a aplicar</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {lineasPanel.map((l) => (
                  <tr key={l.clave} style={{ borderBottom: "1px solid #eef1f4" }}>
                    <td style={{ padding: "6px 8px" }}>
                      {l.label}
                      {!l.esPropio && (
                        <span style={{ marginLeft: 6, color: "#e67e22", fontSize: "0.66rem", fontWeight: 600 }}>
                          otra torre
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "#666" }}>
                      {l.cuotaRef != null ? formatMonto(l.cuotaRef, l.esUSD ? "US$" : "$") : "—"}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "#666" }}>{formatMonto(l.aplicado)}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>
                      <input
                        type="number"
                        min="0"
                        value={montos[l.clave] ?? 0}
                        onChange={(e) => setMontos((prev) => ({ ...prev, [l.clave]: e.target.value }))}
                        style={{
                          width: 120,
                          padding: "3px 6px",
                          textAlign: "right",
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          fontSize: "0.78rem",
                        }}
                      />
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => handleAplicarResto(l.clave)}
                        title="Aplicar el saldo restante a esta línea"
                        style={{
                          fontSize: "0.66rem",
                          padding: "2px 8px",
                          border: "1px solid #2c3e50",
                          borderRadius: 4,
                          background: "transparent",
                          color: "#2c3e50",
                          cursor: "pointer",
                        }}
                      >
                        Aplicar resto
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: validacion.ok ? "#1e8449" : "#c0392b",
                }}
              >
                Sin aplicar: {formatMonto(validacion.saldo)}
              </span>
              {!validacion.ok && <span style={{ fontSize: "0.75rem", color: "#c0392b" }}>{validacion.errores[0]}</span>}
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setFacturaSelId(null)}
                  disabled={guardando}
                  style={{
                    fontSize: "0.78rem",
                    padding: "5px 14px",
                    border: "1px solid #b0b7bf",
                    borderRadius: 4,
                    background: "transparent",
                    color: "#555",
                    cursor: "pointer",
                  }}
                >
                  Cambiar factura
                </button>
                <button
                  type="button"
                  onClick={handleGuardarPanel}
                  disabled={guardando || !validacion.ok}
                  style={{
                    fontSize: "0.78rem",
                    padding: "5px 16px",
                    border: "none",
                    borderRadius: 4,
                    background: guardando || !validacion.ok ? "#bdc3c7" : "#27ae60",
                    color: "#fff",
                    cursor: guardando || !validacion.ok ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  {guardando ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GRID compartido: ROW 1 (meses) + ROW 2 (estado + búsqueda) — mismas columnas */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(13, 1fr)",
            gap: 4,
            marginBottom: 12,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {/* ── ROW 1 ── */}
          <button
            type="button"
            onClick={() => setFiltroMes(null)}
            style={{
              ...pillBase,
              background: filtroMes === null ? "#2c3e50" : "#f0f2f4",
              color: filtroMes === null ? "#fff" : "#555",
              borderColor: filtroMes === null ? "#2c3e50" : "#dee2e6",
              whiteSpace: "nowrap",
            }}
          >
            Todo {anoNum}
          </button>

          {MESES_LABELS.map((label, idx) => {
            const mes = idx + 1;
            const count = countsByMonth[idx];
            const activo = filtroMes === mes;
            const esMesVenta = mes === mesOportunidad;
            const disabled = count === 0;
            return (
              <button
                key={mes}
                type="button"
                onClick={() => !disabled && setFiltroMes(activo ? null : mes)}
                disabled={disabled}
                title={esMesVenta ? "Mes de la venta" : undefined}
                style={{
                  ...pillBase,
                  flex: 1,
                  minWidth: 0,
                  textAlign: "center",
                  background: activo ? "#2c3e50" : "transparent",
                  color: activo ? "#fff" : disabled ? "#ccc" : "#444",
                  borderColor: activo ? "#2c3e50" : esMesVenta ? "#e67e22" : disabled ? "#eee" : "#dee2e6",
                  borderWidth: esMesVenta && !activo ? 2 : 1,
                  cursor: disabled ? "default" : "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {label}
                {count > 0 && (
                  <span
                    style={{
                      marginLeft: 4,
                      background: activo ? "rgba(255,255,255,0.25)" : esMesVenta ? "#e67e22" : "#e9ecef",
                      color: activo || esMesVenta ? "#fff" : "#555",
                      borderRadius: 10,
                      padding: "0 5px",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}

          {/* ── ROW 2 ── */}
          {[
            { value: "", label: "Todas" },
            { value: "pendiente", label: "Pendiente" },
            { value: "vinculada", label: "Vinculada" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFiltroEstado(opt.value)}
              style={{
                ...pillBase,
                textAlign: "center",
                background: filtroEstado === opt.value ? "#2c3e50" : "transparent",
                color: filtroEstado === opt.value ? "#fff" : "#666",
                borderColor: filtroEstado === opt.value ? "#2c3e50" : "#dee2e6",
              }}
            >
              {opt.label}
            </button>
          ))}

          <input
            type="text"
            placeholder="Buscar razón social o folio..."
            value={filtroBusqueda}
            onChange={(e) => setFiltroBusqueda(e.target.value)}
            style={{
              gridColumn: "span 10",
              padding: "4px 10px",
              fontSize: "0.75rem",
              border: "1px solid #dee2e6",
              borderRadius: 4,
            }}
          />
        </div>

        {cargando && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>Cargando documentos del {enteFiscal}...</div>
        )}

        {!cargando && dtes.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
            No hay documentos del {enteFiscal} para este período.
          </div>
        )}

        {!cargando &&
          dtes.length > 0 &&
          (() => {
            const dtesFiltrados = dtes.filter((dte) => {
              if (filtroMes !== null) {
                if (!dte.fecha_emision) return false;
                if (parseInt(dte.fecha_emision.split("-")[1], 10) !== filtroMes) return false;
              }
              if (filtroEstado === "pendiente") {
                const vincs = dte.facturas_sii_vinculaciones || [];
                if (vincs.length > 0) return false;
                if (dte.estado_vinculacion === "vinculada") return false;
              }
              if (filtroEstado === "vinculada") {
                const vincs = dte.facturas_sii_vinculaciones || [];
                if (vincs.length === 0 && dte.estado_vinculacion !== "vinculada") return false;
              }
              if (filtroBusqueda) {
                const texto = filtroBusqueda.toLowerCase();
                const nombre = (dte.razon_social_receptor || "").toLowerCase();
                if (!nombre.includes(texto) && !String(dte.folio).includes(texto)) return false;
              }
              return true;
            });

            if (dtesFiltrados.length === 0) {
              return (
                <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
                  No hay documentos que coincidan con los filtros.
                </div>
              );
            }

            return (
              <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#2c3e50" }}>
                    <th style={thStyle}>Tipo</th>
                    <th style={thStyle}>Folio</th>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>Período</th>
                    <th style={thStyle}>RUT</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>Razón Social</th>
                    <th style={thStyle}>Neto</th>
                    <th style={thStyle}>Total</th>
                    <th style={thStyle}>N. Crédito</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {dtesFiltrados.map((dte) => {
                    const vinculadaAEsta = estaVinculadaAEsta(dte);
                    const vinculadaAOtra =
                      !vinculadaAEsta &&
                      ((dte.facturas_sii_vinculaciones || []).length > 0 || dte.estado_vinculacion === "vinculada");
                    const esNC = esNotaCredito(dte);
                    const anulada = facturasAnuladas.has(Number(dte.id));
                    const ncFolio = ncFolioPorFactura.get(Number(dte.id));
                    const seleccionada = esCarteleriaOp && facturaSelId === dte.id;

                    return (
                      <tr
                        key={dte.id}
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          background: seleccionada ? "#eef6ff" : undefined,
                        }}
                      >
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          <span style={{ fontWeight: 600, color: esNC ? "#c0392b" : "#555", fontSize: "0.72rem" }}>
                            {tipoDocumentoCorto(dte)}
                          </span>
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: 600 }}>{dte.folio}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>{formatFecha(dte.fecha_emision)}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontSize: "0.72rem", color: "#555" }}>
                          {formatPeriodo(dte)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>{formatRut(dte.rut_receptor)}</td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={dte.razon_social_receptor}
                        >
                          {dte.razon_social_receptor}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {formatMonto(dte.monto_neto || dte.monto_exento)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right", fontWeight: 600 }}>
                          {formatMonto(dte.monto_total)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center", fontSize: "0.72rem" }}>
                          {ncFolio != null ? (
                            <span
                              style={{ color: "#c0392b", fontWeight: 600 }}
                              title="Anulada por esta nota de crédito"
                            >
                              NC {ncFolio}
                            </span>
                          ) : (
                            <span style={{ color: "#ccc" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {vinculadaAEsta ? (
                            <span style={{ color: "#27ae60", fontWeight: 600, fontSize: "0.7rem" }}>Vinculada ✓</span>
                          ) : vinculadaAOtra ? (
                            <span style={{ color: "#e67e22", fontSize: "0.7rem" }}>Ya vinculada</span>
                          ) : (
                            <span style={{ color: "#999", fontSize: "0.7rem" }}>
                              {(dte.estado_vinculacion || "").charAt(0).toUpperCase() +
                                (dte.estado_vinculacion || "").slice(1)}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {esNC ? (
                            <span
                              style={{ color: "#999", fontSize: "0.68rem", fontStyle: "italic" }}
                              title="Una nota de crédito no respalda una venta"
                            >
                              No aplica
                            </span>
                          ) : anulada ? (
                            <span
                              style={{ color: "#c0392b", fontSize: "0.68rem", fontWeight: 600 }}
                              title={`Factura anulada por NC ${ncFolio} (anula_error: fue un error y se reemitió) — no puede respaldar una venta`}
                            >
                              No vinculable
                            </span>
                          ) : esCarteleriaOp ? (
                            // Cartelería: la acción es SELECCIONAR la factura → el panel de atribución de
                            // arriba reparte su neto por línea (vincular/desvincular/redistribuir por linea_id).
                            // Si el concepto ya está vinculado a ESTA factura, se conserva el desvincular de
                            // un clic (igual que hoy), además de poder editar el reparto en el panel.
                            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                              <button
                                onClick={() => setFacturaSelId(dte.id)}
                                style={{
                                  fontSize: "0.72rem",
                                  padding: "3px 10px",
                                  border: "1px solid #2c3e50",
                                  borderRadius: 4,
                                  background: seleccionada ? "#2c3e50" : "transparent",
                                  color: seleccionada ? "#fff" : "#2c3e50",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                {seleccionada ? "Seleccionada" : "Seleccionar"}
                              </button>
                              {vinculadaAEsta && (
                                <button
                                  onClick={() => handleDesvincular(dte)}
                                  disabled={procesando === dte.id}
                                  style={{
                                    fontSize: "0.72rem",
                                    padding: "3px 10px",
                                    border: "1px solid #c0392b",
                                    borderRadius: 4,
                                    background: "transparent",
                                    color: "#c0392b",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  Desvincular
                                </button>
                              )}
                            </div>
                          ) : vinculadaAEsta ? (
                            <button
                              onClick={() => handleDesvincular(dte)}
                              disabled={procesando === dte.id}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "1px solid #c0392b",
                                borderRadius: 4,
                                background: "transparent",
                                color: "#c0392b",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              Desvincular
                            </button>
                          ) : vinculadaAOtra ? (
                            <button
                              onClick={() => handleEditar(dte)}
                              disabled={procesando === dte.id || conceptoYaVinculado()}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "1px solid #e67e22",
                                borderRadius: 4,
                                background: conceptoYaVinculado() ? "#bdc3c7" : "transparent",
                                color: conceptoYaVinculado() ? "#fff" : "#e67e22",
                                cursor: conceptoYaVinculado() ? "not-allowed" : "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {procesando === dte.id ? "..." : "Editar"}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleVincularVenta(dte)}
                              disabled={procesando === dte.id || conceptoYaVinculado()}
                              style={{
                                fontSize: "0.72rem",
                                padding: "3px 10px",
                                border: "none",
                                borderRadius: 4,
                                background: conceptoYaVinculado() ? "#bdc3c7" : "#2c3e50",
                                color: "#fff",
                                cursor: conceptoYaVinculado() ? "not-allowed" : "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {procesando === dte.id ? "..." : "Vincular"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SeleccionarDTEModal;
